// src/components/dashboard/EmailDetailsModal.tsx
import React from 'react';
import { X, Mail, Send, Calendar, Hash, FileText, AlertCircle } from 'lucide-react';
import { Email } from '../../types';
import StatusBadge from '../common/StatusBadge';

interface EmailDetailsModalProps {
  email: Email | null;
  isOpen: boolean;
  onClose: () => void;
  onRetry?: (emailId: string) => void;
  onCancel?: (emailId: string) => void;
}

const EmailDetailsModal: React.FC<EmailDetailsModalProps> = ({
  email,
  isOpen,
  onClose,
  onRetry,
  onCancel
}) => {
  if (!isOpen || !email) return null;

  const getPriorityLabel = (priority: number) => {
    const labels: { [key: number]: string } = {
      1: 'Critical',
      2: 'High',
      3: 'Normal',
      4: 'Low',
      5: 'Bulk'
    };
    return labels[priority] || 'Unknown';
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Email Details</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Email ID and Reference */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    <Hash size={14} className="inline mr-1" />
                    Email ID
                  </label>
                  <p className="text-sm font-mono bg-gray-50 p-2 rounded">
                    {email.emailId}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    <FileText size={14} className="inline mr-1" />
                    Client Reference
                  </label>
                  <p className="text-sm font-mono bg-gray-50 p-2 rounded">
                    {email.clientReference || '-'}
                  </p>
                </div>
              </div>

              {/* Priority and Status */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Priority
                  </label>
                  <p className="text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      email.priority === 1 ? 'bg-red-100 text-red-800' :
                      email.priority === 2 ? 'bg-orange-100 text-orange-800' :
                      email.priority === 3 ? 'bg-blue-100 text-blue-800' :
                      email.priority === 4 ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {getPriorityLabel(email.priority)}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    SMTP Status
                  </label>
                  <StatusBadge status={email.smtpStatus} type="smtp" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Delivery Status
                  </label>
                  <StatusBadge status={email.deliveryStatus} type="delivery" />
                </div>
              </div>

              {/* Email Addresses */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    <Mail size={14} className="inline mr-1" />
                    From
                  </label>
                  <p className="text-sm bg-gray-50 p-2 rounded">{email.fromAddress}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    <Send size={14} className="inline mr-1" />
                    To
                  </label>
                  <p className="text-sm bg-gray-50 p-2 rounded">{email.toAddress}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Subject
                  </label>
                  <p className="text-sm bg-gray-50 p-2 rounded">
                    {email.subject || '(no subject)'}
                  </p>
                </div>
              </div>

              {/* Timestamps */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">
                  <Calendar size={14} className="inline mr-1" />
                  Timeline
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Queued At
                    </label>
                    <p className="text-sm">{formatDate(email.queuedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      SMTP Completed
                    </label>
                    <p className="text-sm">{formatDate(email.smtpCompletedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Delivery Completed
                    </label>
                    <p className="text-sm">{formatDate(email.deliveryCompletedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Attempts
                    </label>
                    <p className="text-sm">{email.attemptCount} / {email.maxAttempts}</p>
                  </div>
                </div>
              </div>

              {/* Error Information */}
              {(email.smtpResponseMessage || email.deliveryStatusMessage || email.lastErrorMessage) && (
                <div className="space-y-3 mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">
                    <AlertCircle size={14} className="inline mr-1" />
                    Response Details
                  </h3>
                  {email.smtpStatusCode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        SMTP Response Code
                      </label>
                      <p className="text-sm font-mono bg-red-50 p-2 rounded text-red-700">
                        {email.smtpStatusCode}
                      </p>
                    </div>
                  )}
                  {email.smtpResponseMessage && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        SMTP Response
                      </label>
                      <p className="text-sm bg-gray-50 p-2 rounded">
                        {email.smtpResponseMessage}
                      </p>
                    </div>
                  )}
                  {email.deliveryStatusMessage && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Delivery Message
                      </label>
                      <p className="text-sm bg-gray-50 p-2 rounded">
                        {email.deliveryStatusMessage}
                      </p>
                    </div>
                  )}
                  {email.lastErrorMessage && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Last Error
                      </label>
                      <p className="text-sm bg-red-50 p-2 rounded text-red-700">
                        {email.lastErrorMessage}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Status Flags */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Paused:</span>
                  <span className={email.isPaused ? 'text-yellow-600' : 'text-gray-600'}>
                    {email.isPaused ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              {email.smtpStatus === 'Queued' && onCancel && (
                <button
                  onClick={() => onCancel(email.emailId)}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Cancel Email
                </button>
              )}
              {email.smtpStatus === 'Failed' && onRetry && (
                <button
                  onClick={() => onRetry(email.emailId)}
                  className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Retry Email
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailDetailsModal;